<template>
  <div
    class="cbsf"
    :class="{ 'cbsf--active': active }"
    @click="$emit('activate')"
  >

    <!-- ─── Header: editable name + close ─── -->
    <div
      class="cbsf__header"
      @click.stop
    >
      <span
        class="cbsf__toggle"
        title="Свернуть / Развернуть"
        @click.stop="$emit('toggle-collapse')"
      >
        <feather-icon
          :icon="item.collapsed ? 'ChevronRightIcon' : 'ChevronDownIcon'"
          size="13"
        />
      </span>
      <feather-icon
        icon="HashIcon"
        size="13"
        class="cbsf__hdr-icon"
      />
      <input
        v-if="editingName"
        ref="nameInput"
        v-model="nameInputVal"
        class="cbsf__name-input"
        @keydown.enter.prevent="saveName"
        @keydown.esc.prevent="cancelNameEdit"
        @blur="saveName"
        @click.stop
      >
      <span
        v-else
        class="cbsf__name"
        title="Двойной клик — редактировать"
        @dblclick.stop="startNameEdit"
      >{{ item.formulaName || item.command }}</span>
      <span
        class="cbsf__close"
        title="Убрать"
        @click.stop="$emit('remove')"
      >
        <feather-icon
          icon="XIcon"
          size="13"
        />
      </span>
    </div>

    <!-- ─── Body ─── -->
    <div
      v-if="!item.collapsed"
      class="cbsf__body"
    >

      <!-- id row -->
      <div class="cbsf__field">
        <span class="cbsf__flabel">id:</span>
        <span class="cbsf__fval">{{ item.formulaId }}</span>
      </div>

      <!-- sid row -->
      <div class="cbsf__field">
        <span class="cbsf__flabel">sid:</span>
        <span class="cbsf__fval cbsf__fval--muted">{{ item.formulaSid || '—' }}</span>
      </div>

      <!-- ─── Parts section ─── -->
      <div class="cbsf__parts">

        <!-- Parts header row -->
        <div
          class="cbsf__parts-hdr"
          @click.stop="toggleParts"
        >
          <feather-icon
            :icon="partsExpanded ? 'ChevronDownIcon' : 'ChevronRightIcon'"
            size="12"
            class="cbsf__parts-toggle"
          />
          <span class="cbsf__parts-label">Части</span>
          <span
            v-if="parts.length"
            class="cbsf__parts-cnt"
          >{{ parts.length }}</span>
          <b-spinner
            v-if="partsLoading || partSaving"
            small
            variant="primary"
            class="cbsf__parts-spin"
          />
          <span
            class="cbsf__parts-add"
            title="Добавить"
            @click.stop="addPart"
          >
            <feather-icon
              icon="PlusIcon"
              size="12"
            />
          </span>
        </div>

        <!-- Parts list -->
        <template v-if="partsExpanded">
          <div
            v-if="!partsLoading && parts.length === 0"
            class="cbsf__parts-empty"
          >
            нет частей
          </div>

          <div
            v-for="(part, pi) in parts"
            :key="getPartId(part)"
            class="cbsf__part"
          >
            <!-- position -->
            <span class="cbsf__part-num">{{ pi + 1 }}</span>
            <!-- db id -->
            <span class="cbsf__part-id">#{{ getPartId(part) }}</span>

            <!-- type word -->
            <div
              class="cbsf__part-type"
              @click.stop
            >
              <span
                class="cbsf__type-word"
                :class="{ 'cbsf__type-word--empty': !getPartTypeName(part) }"
                @click.stop="openTypePicker(pi)"
              >{{ getPartTypeName(part) || '(тип)' }}</span>
              <div
                v-if="activeTypeRow === pi"
                class="cbsf__type-panel"
                @click.stop
              >
                <input
                  v-model="typeSearch"
                  class="cbsf__type-search"
                  placeholder="Поиск..."
                  autocomplete="off"
                  @input="onTypeSearch"
                  @keydown.esc.prevent="closeTypePicker"
                  @keydown.enter.prevent="pickTypeFirst(pi)"
                >
                <div
                  v-if="typeFiltered.length"
                  class="cbsf__type-list"
                >
                  <div
                    v-for="opt in typeFiltered"
                    :key="opt.value"
                    class="cbsf__type-opt"
                    @click.stop="pickType(pi, opt)"
                  >{{ opt.title }}</div>
                </div>
                <div
                  v-else-if="typeSearch"
                  class="cbsf__type-empty"
                >
                  Не найдено
                </div>
              </div>
            </div>

            <!-- value: sql → text input, others → picker -->
            <div
              class="cbsf__part-value"
              @click.stop
            >

              <!-- sql type: inline text editing -->
              <template v-if="isPartTypeSql(part)">
                <input
                  v-if="editingPartValue === pi"
                  v-model="editingValueText"
                  class="cbsf__part-sql-input"
                  @keydown.enter.prevent="savePartSqlValue(pi)"
                  @keydown.esc.prevent="cancelPartValueEdit"
                  @blur="onSqlBlur(pi)"
                  @click.stop
                >
                <span
                  v-else
                  class="cbsf__part-sql-val"
                  :title="getPartValue(part)"
                  @dblclick.stop="startPartValueEdit(pi)"
                >{{ truncate(getPartValue(part), 48) || '—' }}</span>
              </template>

              <!-- other types: picker -->
              <cbs-inline-picker
                v-else
                :options="getPartValueOptions(part)"
                :display-val="getPartValueDisplay(part)"
                label-key="title"
                placeholder="поиск..."
                :empty-label="'—'"
                @select="opt => savePartValueRef(pi, opt)"
              />
            </div>

            <!-- delete -->
            <span
              class="cbsf__part-del"
              title="Удалить"
              @click.stop="deletePart(pi)"
            >
              <feather-icon
                icon="MinusIcon"
                size="12"
              />
            </span>

          </div>
        </template>

      </div>
    </div>
  </div>
</template>

<script>
import { BSpinner } from 'bootstrap-vue'
import CbsInlinePicker from '@/cubus/components/inline-picker/CbsInlinePicker.vue'
import useCubus from '@/cubus/services/useCubus'

export default {
  name: 'CbsConstructorFormulaCard',
  components: { CbsInlinePicker, BSpinner },
  props: {
    item: { type: Object, required: true },
    active: { type: Boolean, default: false },
  },
  data() {
    return {
      parts: [],
      partsLoading: false,
      partsExpanded: false,
      partSaving: false,
      // part types
      partTypeOptions: [], // { value, title, sid, valueObjectId }
      partTypesLoaded: false,
      // value options cache by objectId
      partValueOptionsCache: {},
      // entity list for 'entity' type parts
      allEntities: [],
      allEntitiesLoading: false,
      // inline name editing
      editingName: false,
      nameInputVal: '',
      nameEditCanceling: false,
      // sql value editing
      editingPartValue: -1,
      editingValueText: '',
      valueEditCanceling: false,
      // type picker
      activeTypeRow: -1,
      typeSearch: '',
      typeFiltered: [],
    }
  },
  watch: {
    'item.formulaId': {
      immediate: true,
      handler(id) { if (id) this.loadParts() },
    },
  },
  mounted() {
    this._outsideClick = e => {
      if (this.activeTypeRow >= 0 && !e.target.closest('.cbsf__part-type')) this.closeTypePicker()
    }
    document.addEventListener('click', this._outsideClick)
  },
  beforeDestroy() {
    document.removeEventListener('click', this._outsideClick)
  },
  methods: {

    // ─── Name editing ───────────────────────────────────────────────────────

    startNameEdit() {
      this.nameInputVal = this.item.formulaName || ''
      this.nameEditCanceling = false
      this.editingName = true
      this.$nextTick(() => {
        if (this.$refs.nameInput) {
          this.$refs.nameInput.focus()
          this.$refs.nameInput.select()
        }
      })
    },
    cancelNameEdit() {
      this.nameEditCanceling = true
      this.editingName = false
      this.$nextTick(() => { this.nameEditCanceling = false })
    },
    saveName() {
      if (this.nameEditCanceling) return
      this.editingName = false
      const val = this.nameInputVal.trim()
      if (val && val !== (this.item.formulaName || '')) {
        this.$emit('update-field', { field: 'name', value: val, itemField: 'formulaName' })
      }
    },

    // ─── Parts loading ───────────────────────────────────────────────────────

    toggleParts() {
      this.partsExpanded = !this.partsExpanded
      if (this.partsExpanded && !this.partTypesLoaded) this.loadPartTypes()
    },

    async loadParts() {
      if (!this.item.formulaId) return
      this.partsLoading = true
      try {
        const query = await useCubus.loadQuery(this, {
          objectsid: 'formulapart',
          keymode: 'sid',
          limit: 1000,
          offset: 0,
          filter: {
            node: {
              isactive: true,
              oper: { sid: 'and' },
              nodes: [],
              conds: [{
                isactive: true,
                oper: { sid: 'equal' },
                args: [
                  { type: { sid: 'entitysid' }, value: 'formula' },
                  { type: { sid: 'value' }, value: String(this.item.formulaId) },
                ],
              }],
            },
          },
        })
        if (query && query.recordset) {
          this.parts = query.recordset.records || []
          if (this.parts.length > 0) {
            this.partsExpanded = true
            if (!this.partTypesLoaded) this.loadPartTypes()
          }
        }
      } finally {
        this.partsLoading = false
      }
    },

    async loadPartTypes() {
      this.partTypesLoaded = true
      try {
        const query = await useCubus.loadQuery(this, { objectsid: 'formulaparttype', limit: 200, offset: 0 })
        if (query && query.recordset) {
          this.partTypeOptions = (query.recordset.records || []).map(r => {
            const cells = r.cells || r
            const sid = cells.sid ? (cells.sid.value || '') : ''
            const valueObjectId = cells.valueobject ? (cells.valueobject.value || null) : null
            return {
              value: cells.id ? cells.id.value : null,
              title: cells.name ? (cells.name.value || sid) : sid,
              sid,
              valueObjectId,
            }
          }).filter(o => o.value != null)
        }
      } catch {
        // parttype object may not exist in this domain
      }
    },

    async loadAllEntities() {
      if (this.allEntitiesLoading || this.allEntities.length > 0) return
      this.allEntitiesLoading = true
      try {
        const query = await useCubus.loadQuery(this, { objectsid: 'entity', limit: 10000, offset: 0 })
        if (query && query.recordset) {
          this.allEntities = (query.recordset.records || []).map(rec => {
            const c = rec.cells || rec
            return {
              value: c.id ? c.id.value : null,
              title: c.name ? (c.name.value || '') : (c.sid ? (c.sid.value || '') : ''),
            }
          }).filter(e => e.value != null && e.title !== '')
        }
      } finally {
        this.allEntitiesLoading = false
      }
    },

    async loadValueOptions(objectId) {
      this.$set(this.partValueOptionsCache, objectId, [])
      try {
        const query = await useCubus.loadQuery(this, { objectid: objectId, limit: 500, offset: 0 })
        if (query && query.recordset) {
          const opts = (query.recordset.records || []).map(r => {
            const cells = r.cells || r
            return {
              value: cells.id ? cells.id.value : null,
              title: cells.name ? (cells.name.value || '') : (cells.sid ? (cells.sid.value || '') : ''),
            }
          }).filter(o => o.value != null && o.title !== '')
          this.$set(this.partValueOptionsCache, objectId, opts)
        }
      } catch {
        // ignore
      }
    },

    // ─── Part cell helpers ───────────────────────────────────────────────────

    getPartId(part) {
      const cells = part.cells || part
      return cells.id ? cells.id.value : '?'
    },

    getPartTypeId(part) {
      const cells = part.cells || part
      return cells.formulaparttype ? cells.formulaparttype.value : null
    },

    getPartTypeName(part) {
      const cells = part.cells || part
      if (!cells.formulaparttype) return ''
      const t = cells.formulaparttype
      return (t.title && t.title !== '') ? t.title : String(t.value || '')
    },

    getPartTypeSid(part) {
      const typeId = this.getPartTypeId(part)
      if (typeId == null) return ''
      const opt = this.partTypeOptions.find(o => o.value === typeId)
      return opt ? (opt.sid || '').toLowerCase() : ''
    },

    isPartTypeSql(part) {
      const sid = this.getPartTypeSid(part)
      // default to text when type is not set or types not loaded yet
      return sid === 'sql' || (sid === '' && this.getPartTypeId(part) == null)
    },

    getPartValue(part) {
      const typeSid = this.getPartTypeSid(part)
      if (typeSid == null) return ''
      if (typeSid === 'sql') { return part.sqlstring ? String(part.sqlstring.value || '') : '' }
      if (typeSid === 'entity') { return part.entity ? String(part.entity.value) || '' : '' }
      // if (!cells.value) return ''
      // return String(cells.value.value != null ? cells.value.value : '')
      return ''
    },

    getPartValueDisplay(part) {
      const typeSid = this.getPartTypeSid(part)
      if (typeSid == null) return ''
      if (typeSid === 'sql') { return part.sqlstring ? String(part.sqlstring.value || '') : '' }
      if (typeSid === 'entity') { return part.entity ? String(part.entity.title) || '' : '' }
      return ''
    },

    getPartValueOptions(part) {
      const typeId = this.getPartTypeId(part)
      if (typeId == null) return []
      const typeSid = this.getPartTypeSid(part)
      if (typeSid === 'entity') {
        if (!this.allEntities.length && !this.allEntitiesLoading) this.loadAllEntities()
        return this.allEntities
      }
      const typeOpt = this.partTypeOptions.find(o => o.value === typeId)
      if (!typeOpt || !typeOpt.valueObjectId) return []
      const objectId = typeOpt.valueObjectId
      if (!(objectId in this.partValueOptionsCache)) {
        this.loadValueOptions(objectId)
        return []
      }
      return this.partValueOptionsCache[objectId] || []
    },

    truncate(str, len) {
      if (!str) return ''
      return str.length > len ? `${str.slice(0, len)}…` : str
    },

    // ─── SQL value editing ───────────────────────────────────────────────────

    startPartValueEdit(pi) {
      const part = this.parts[pi]
      if (!part) return
      this.valueEditCanceling = false
      this.editingValueText = this.getPartValue(part)
      this.editingPartValue = pi
      this.$nextTick(() => {
        const input = this.$el.querySelector('.cbsf__part-sql-input')
        if (input) input.focus()
      })
    },

    cancelPartValueEdit() {
      this.valueEditCanceling = true
      this.editingPartValue = -1
      this.editingValueText = ''
      this.$nextTick(() => { this.valueEditCanceling = false })
    },

    onSqlBlur(pi) {
      if (this.valueEditCanceling) return
      this.savePartSqlValue(pi)
    },

    async savePartSqlValue(pi) {
      if (this.valueEditCanceling) return
      const idx = this.editingPartValue
      this.editingPartValue = -1
      if (idx !== pi) return
      const val = this.editingValueText
      this.editingValueText = ''
      await this.savePartFieldRaw(pi, 'sqlstring', val)
    },

    // ─── Type picker ─────────────────────────────────────────────────────────

    openTypePicker(pi) {
      if (this.activeTypeRow === pi) { this.closeTypePicker(); return }
      if (!this.partTypesLoaded) this.loadPartTypes()
      this.typeSearch = ''
      this.typeFiltered = this.partTypeOptions.slice()
      this.activeTypeRow = pi
      this.$nextTick(() => {
        const input = this.$el.querySelector('.cbsf__type-search')
        if (input) input.focus()
      })
    },
    closeTypePicker() {
      this.activeTypeRow = -1
      this.typeSearch = ''
      this.typeFiltered = []
    },
    onTypeSearch() {
      if (!this.typeSearch.trim()) { this.typeFiltered = this.partTypeOptions.slice(); return }
      const re = new RegExp(
        this.typeSearch.trim().toLowerCase().split(/\s+/).map(p => p.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
          .join('.*'),
      )
      this.typeFiltered = this.partTypeOptions.filter(o => re.test((o.title || '').toLowerCase()))
    },
    pickTypeFirst(pi) {
      if (this.typeFiltered.length) this.pickType(pi, this.typeFiltered[0])
    },
    pickType(pi, opt) {
      this.closeTypePicker()
      this.savePartType(pi, opt)
    },

    // ─── Part save operations ────────────────────────────────────────────────

    async savePartType(pi, opt) {
      if (!opt || opt.value == null) return
      await this.savePartFieldRaw(pi, 'parttype', opt.value)
    },

    async savePartValueRef(pi, opt) {
      if (!opt || opt.value == null) return
      const part = this.parts[pi]
      const typeSid = part ? this.getPartTypeSid(part) : ''
      const fieldKey = typeSid === 'entity' ? 'entity' : 'value'
      await this.savePartFieldRaw(pi, fieldKey, opt.value)
    },

    async savePartFieldRaw(pi, fieldKey, val) {
      const part = this.parts[pi]
      if (!part) return
      const cells = part.cells || part
      const partId = cells.id ? cells.id.value : null
      if (partId == null) return
      await useCubus.saveRecord(this, {
        objectsid: 'formulapart',
        keymode: 'sid',
        record: {
          id: { value: partId, status: 'loaded' },
          [fieldKey]: { value: val, status: 'changed' },
        },
      })
      await this.loadParts()
    },

    // ─── Add / Delete parts ──────────────────────────────────────────────────

    async addPart() {
      if (!this.item.formulaId || this.partSaving) return
      this.partSaving = true
      this.partsExpanded = true
      if (!this.partTypesLoaded) this.loadPartTypes()
      try {
        const result = await useCubus.loadNewRecord(this, { objectsid: 'formulapart' })
        if (!result || !result.record) return
        const rec = result.record.record
        if (rec.formula) { rec.formula.value = this.item.formulaId; rec.formula.status = 'changed' }
        const maxOrder = this.parts.reduce((m, p) => {
          const c = p.cells || p
          return Math.max(m, c.ordernum ? (c.ordernum.value || 0) : 0)
        }, 0)
        if (rec.ordernum) {
          rec.ordernum.value = Math.ceil((maxOrder + 1) / 100) * 100
          rec.ordernum.status = 'changed'
        }
        await useCubus.saveRecord(this, { objectsid: 'formulapart', keymode: 'sid', record: rec })
        await this.loadParts()
      } finally {
        this.partSaving = false
      }
    },

    async deletePart(pi) {
      const part = this.parts[pi]
      if (!part) return
      const cells = part.cells || part
      const partId = cells.id ? cells.id.value : null
      if (partId == null || this.partSaving) return
      this.partSaving = true
      try {
        await useCubus.saveRecord(this, {
          objectsid: 'formulapart',
          keymode: 'sid',
          record: {
            id: { value: partId, status: 'loaded' },
            del: { value: true, status: 'changed' },
          },
        })
        await this.loadParts()
      } finally {
        this.partSaving = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>

// ─── Card wrapper ────────────────────────────────────────────────────────────

.cbsf {
  border: 1px solid #ebe9f1;
  border-radius: 6px;
  font-size: 13px;
  overflow: visible;
}

// ─── Header ──────────────────────────────────────────────────────────────────

.cbsf__header {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  background: #f8f8f8;
  border-bottom: 1px solid #ebe9f1;
  border-radius: 6px 6px 0 0;
  min-height: 32px;
}

.cbsf__toggle {
  cursor: pointer;
  color: #b9b9c3;
  line-height: 1;
  padding: 2px;
  border-radius: 3px;
  flex-shrink: 0;
  &:hover { color: #5e5873; }
}

.cbsf__hdr-icon {
  color: #28c76f;
  flex-shrink: 0;
}

.cbsf__name {
  flex: 1;
  font-weight: 600;
  font-family: monospace;
  font-size: 13px;
  color: #28c76f;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: default;
  user-select: none;
}

.cbsf__name-input {
  flex: 1;
  font-weight: 600;
  font-family: monospace;
  font-size: 13px;
  color: #5e5873;
  border: 1px solid #7367f0;
  border-radius: 3px;
  padding: 1px 6px;
  outline: none;
  background: #fff;
  min-width: 0;
}

.cbsf__close {
  cursor: pointer;
  color: #b9b9c3;
  line-height: 1;
  padding: 2px;
  border-radius: 3px;
  flex-shrink: 0;
  &:hover { color: #ea5455; background: rgba(234, 84, 85, 0.1); }
}

.cbsf--active .cbsf__header {
  background: #28c76f;
  border-color: #28c76f;
  .cbsf__toggle   { color: rgba(255,255,255,0.7); &:hover { color: #fff; } }
  .cbsf__hdr-icon { color: rgba(255, 255, 255, 0.9); }
  .cbsf__name     { color: #fff; }
  .cbsf__close    { color: rgba(255,255,255,0.7); &:hover { color: #fff; background: rgba(255,255,255,0.15); } }
}

// ─── Body ────────────────────────────────────────────────────────────────────

.cbsf__body {
  padding: 8px 10px;
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.cbsf__field {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 1px 0;
}

.cbsf__flabel {
  color: #b9b9c3;
  font-family: monospace;
  font-size: 11px;
  min-width: 28px;
  flex-shrink: 0;
}

.cbsf__fval {
  color: #5e5873;
  font-family: monospace;
  font-size: 11px;
  &--muted { color: #b9b9c3; }
}

// ─── Parts section ───────────────────────────────────────────────────────────

.cbsf__parts {
  border: 1px solid #ebe9f1;
  border-radius: 4px;
  margin-top: 5px;
  overflow: visible;
}

.cbsf__parts-hdr {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 4px 8px;
  background: #f8f8f8;
  cursor: pointer;
  user-select: none;
  font-size: 12px;
  border-radius: 4px;
  &:hover { background: #f0f0f5; }
}

.cbsf__parts-toggle { color: #b9b9c3; flex-shrink: 0; }
.cbsf__parts-label  { font-weight: 500; color: #5e5873; flex: 1; font-size: 12px; }
.cbsf__parts-cnt    { font-size: 10px; color: #b9b9c3; }

.cbsf__parts-spin {
  width: 10px !important;
  height: 10px !important;
  flex-shrink: 0;
}

.cbsf__parts-add {
  cursor: pointer;
  color: #b9b9c3;
  line-height: 1;
  padding: 2px;
  border-radius: 3px;
  flex-shrink: 0;
  &:hover { color: #28c76f; background: rgba(40, 199, 111, 0.1); }
}

.cbsf__parts-empty {
  padding: 5px 8px;
  font-size: 11px;
  color: #b9b9c3;
}

// ─── Single part row ─────────────────────────────────────────────────────────

.cbsf__part {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 3px 8px;
  border-top: 1px solid #f0f0f0;

  &:first-of-type { border-top: none; }
  &:hover { background: #fafafa; }
}

.cbsf__part-num {
  color: #b9b9c3;
  font-size: 10px;
  font-family: monospace;
  min-width: 12px;
  text-align: right;
  flex-shrink: 0;
}

.cbsf__part-id {
  color: #b9b9c3;
  font-size: 10px;
  font-family: monospace;
  flex-shrink: 0;
}

.cbsf__part-type {
  position: relative;
  flex-shrink: 0;
  width: 88px;
  font-size: 11px;
  overflow: visible;
}

.cbsf__type-word {
  font-size: 11px;
  color: #82868b;
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
  border-bottom: 1px dashed rgba(130, 134, 139, 0.3);
  &:hover { color: #7367f0; border-bottom-color: #7367f0; }
}

.cbsf__type-word--empty {
  color: #b9b9c3;
  font-style: italic;
  border-bottom-color: rgba(185, 185, 195, 0.3);
}

.cbsf__type-panel {
  position: absolute;
  top: 100%;
  left: 0;
  z-index: 20;
  width: 200px;
  background: #fff;
  border: 1px solid #ebe9f1;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 4px;
}

.cbsf__type-search {
  width: 100%;
  font-size: 12px;
  border: 1px solid #d8d6de;
  border-radius: 3px;
  padding: 2px 6px;
  outline: none;
  color: #5e5873;
  background: #fff;
}

.cbsf__type-list {
  max-height: 160px;
  overflow-y: auto;
  margin-top: 4px;
}

.cbsf__type-opt {
  padding: 3px 8px;
  cursor: pointer;
  border-radius: 3px;
  font-size: 11px;
  color: #5e5873;
  &:hover { background: #f8f8f8; }
}

.cbsf__type-empty {
  padding: 6px 8px;
  font-size: 11px;
  color: #b9b9c3;
}

.cbsf__part-value {
  flex: 1;
  min-width: 0;
  overflow: visible;
  font-size: 11px;
}

.cbsf__part-sql-val {
  display: block;
  font-family: monospace;
  font-size: 11px;
  color: #5e5873;
  cursor: text;
  border-radius: 3px;
  padding: 1px 3px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  &:hover { background: rgba(115, 103, 240, 0.06); }
}

.cbsf__part-sql-input {
  width: 100%;
  font-family: monospace;
  font-size: 11px;
  border: 1px solid #7367f0;
  border-radius: 3px;
  padding: 1px 4px;
  outline: none;
  color: #5e5873;
  background: #fff;
}

.cbsf__part-del {
  cursor: pointer;
  color: #b9b9c3;
  line-height: 1;
  padding: 1px 3px;
  border-radius: 2px;
  flex-shrink: 0;
  opacity: 0;
  .cbsf__part:hover & { opacity: 1; }
  &:hover { color: #ea5455; background: rgba(234, 84, 85, 0.1); }
}

// ─── Dark mode ───────────────────────────────────────────────────────────────

.dark-layout {
  .cbsf               { border-color: #3b4253; }
  .cbsf__header       { background: #242b3d; border-color: #3b4253; }
  .cbsf--active .cbsf__header { background: #28c76f; border-color: #28c76f; }
  .cbsf__name-input   { background: #283046; color: #d0d2d6; border-color: #7367f0; }
  .cbsf__fval         { color: #d0d2d6; &--muted { color: #6e6b7b; } }
  .cbsf__parts        { border-color: #3b4253; }
  .cbsf__parts-hdr    { background: #242b3d; border-radius: 4px; &:hover { background: #2d3548; } }
  .cbsf__parts-label  { color: #d0d2d6; }
  .cbsf__part         { border-color: #3b4253; &:hover { background: rgba(255,255,255,0.03); } }
  .cbsf__part-sql-val { color: #d0d2d6; &:hover { background: rgba(115, 103, 240, 0.1); } }
  .cbsf__part-sql-input { background: #283046; color: #d0d2d6; border-color: #7367f0; }
  .cbsf__type-word { color: #676d7d; &:hover { color: #7367f0; } }
  .cbsf__type-word--empty { color: #4b4b5a; }
  .cbsf__type-panel { background: #283046; border-color: #3b4253; box-shadow: 0 2px 8px rgba(0,0,0,0.3); }
  .cbsf__type-search { background: #283046; color: #d0d2d6; border-color: #3b4253; }
  .cbsf__type-opt { color: #d0d2d6; &:hover { background: #3b4253; } }
}
</style>
